import {
  Skeleton,
  SkeletonContextProvider,
  WithSkeleton,
} from '@snack-uikit/skeleton';
import { useMemo } from 'react';
import { NotFound } from 'src/snack/components/ProductsCatalog/NotFound';
import {
  type CatalogProduct,
  type ProductCategory,
} from 'src/types/backendContent';
import {
  type CategoryWithProducts,
  getCategoryProductMap,
} from 'src/utils/getCategoriesWithProducts';

import { CatalogSection } from './CatalogSection';
import s from './ProductsList.module.scss';

const SKELETON_COUNT = 4;

type ProductsListProps = {
  categoryFilter: string | undefined;
  categories: ProductCategory[];
  products: CatalogProduct[];
  loading: boolean;
  searchedPhrase: string;
};

function ProductsListSkeleton() {
  return (
    <div>
      <Skeleton className={s.sectionTitle} width={132} height={28} />

      <div className={s.sectionProducts}>
        {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
          <Skeleton key={index} height={92} borderRadius={0} />
        ))}
      </div>
    </div>
  );
}

const renderCategoryWithProducts =
  // eslint-disable-next-line react/display-name
  (searchedPhrase: string) => (categoryWithProducts: CategoryWithProducts) =>
    (
      <CatalogSection
        key={categoryWithProducts.category.id}
        title={categoryWithProducts.category.title}
        products={categoryWithProducts.products}
        searchedPhrase={searchedPhrase}
      />
    );

export function ProductsList({
  categoryFilter,
  categories,
  products,
  loading,
  searchedPhrase,
}: ProductsListProps) {
  const categoriesWithProducts: CategoryWithProducts[] = useMemo(
    () => getCategoryProductMap(categories, products, categoryFilter),
    [categories, products, categoryFilter],
  );

  return (
    <div className={s.list}>
      <SkeletonContextProvider loading={loading}>
        <WithSkeleton skeleton={<ProductsListSkeleton />}>
          {products.length ? (
            categoriesWithProducts.map(
              renderCategoryWithProducts(searchedPhrase),
            )
          ) : (
            <NotFound />
          )}
        </WithSkeleton>
      </SkeletonContextProvider>
    </div>
  );
}
